import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "@tanstack/react-router";
import './CollectionsHeader.css';

import useCollection from "./useCollection";
import { useAppKit } from '@reown/appkit/react';

import CollectionInfoHeader from "../../components/CollectionInfoHeader";
import CollectionHero from "./CollectionHero";
import VolumeStats from "../../components/VolumeStats";
import NFTPreviewGrid from "../../components/NFTPreviewGrid";
import Comments from "../../components/Comments/index.jsx";
import { OwnedTokens } from "./OwnedTokens";
import { CollectionActions } from "./CollectionActions.jsx";
import TraitRarityStats from "../../components/TraitRarityStats";
import NftDetailModal from "../../components/NftDetailModal";
import SaleParticularsModal from "../../components/SaleParticularsModal";
import SellItemModal from "../../components/SellItemModal";
import { chainData } from "../../chains.js";
import Breadcrumb from "../../components/Breadcrumb";

const CollectionContainer = ({ collectionId, initialCollectionInfo, initialNfts, initialVolData, collectionName, logo }) => {
  const location = useLocation();
  const stateImage = (location.state as any)?.imgSrc || (location.state as any)?.imageUrl || null;
  const {
    cDat, files, show, activeItem, mContent, moTitle, volData, volPer,
    needsNetworkSwitch, failedImages, searchTerm, setSearchTerm,
    setShow, setMC, doSearch, patchTokenListing, pagination, getInf, alterPos, uploadCollImg,
    mintItem, getItemAndShowModal, handleNetworkSwitch, handlePeriodChange, navigate
  } = useCollection(collectionId, {
    initialCInfo: initialCollectionInfo,
    initialNfts: initialNfts,
    initialVolData: initialVolData,
  });

  const [mc] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('items');
  const [filterCollapsed, setFilterCollapsed] = useState(true);
  const [currentPosition, setCurrentPosition] = useState(0);

  const { wallet } = useAppKit();

  const resolvedCollectionName = useMemo(() => {
    if (collectionName) return collectionName
    if (cDat?.title) return cDat.title
    if (cDat?.name) return cDat.name
    return String(collectionId)
  }, [collectionName, cDat, collectionId])

  const handleItemSelect = (file, resolvedSrc) => {
    // Pass the grid's already-loaded metadata + image URL so the modal skips
    // the nftMetaByCidItem fetch and the extra CDN request entirely. `file.aid`
    // is the token's auctionID (0/undefined when unlisted) and drives the
    // modal's Buy button; `file.contractAddress` is needed for the on-chain
    // sale reads the Buy button opens.
    getItemAndShowModal(file.coll, file.tokId, resolvedSrc || file.imgurl, file.meta, file.aid, file.contractAddress, file.ownerAddress);
  };

  // Buy: close the detail modal and open the on-chain sale-particulars modal
  // in its place. `target` carries the auction id + NFT contract.
  const [saleTarget, setSaleTarget] = useState<any>(null);
  const [showSale, setShowSale] = useState(false);

  // Sell (owner, not listed) opens the fixed-price listing form; Manage (owner,
  // already listed) opens the same sale-details modal read-only. Both close the
  // detail modal first so only one modal is ever on screen.
  const [sellTarget, setSellTarget] = useState<any>(null);
  const [showSell, setShowSell] = useState(false);
  const [manageMode, setManageMode] = useState(false);

  // The sale-particulars modal must read the chain the TOKEN lives on.
  // `cDat.network` is a chain NAME (e.g. "Flare"), not a chainData index or
  // id, so pass the actual EVM chain id and let the modal resolve its own row.
  const withChain = (target: any) => ({
    ...target,
    chainIndex: cDat?.network,
    chainId: cDat?.chainId !== undefined ? parseInt(String(cDat.chainId), 10) : undefined,
    collectionId: cDat?.id,
  });

  const handleBuy = (target: any) => {
    if (!target) return;
    setShow(false);
    setManageMode(false);
    setSaleTarget(withChain(target));
    setShowSale(true);
  };

  const handleSell = (target: any) => {
    if (!target) return;
    setShow(false);
    setSellTarget(withChain({ ...target, meta: activeItem?.item?.meta }));
    setShowSell(true);
  };

  const handleManage = (target: any) => {
    if (!target) return;
    setShow(false);
    setManageMode(true);
    setSaleTarget(withChain(target));
    setShowSale(true);
  };

  // Listed successfully on-chain: drop the sell modal and update THIS token's
  // card in place so the "For Sale" badge and price appear immediately.
  //
  // patchTokenListing, not doSearch: a full refetch re-requests every image on
  // the page and can reorder the grid, which is a lot of churn for one card.
  // The token is known to be on the current page — the user just clicked it.
  const handleListed = (listingId?: string) => {
    setShowSell(false);
    setSellTarget(null);
    const tokId = sellTarget?.tokenId ?? activeItem?.item?.tokId;
    if (tokId !== undefined && tokId !== null) {
      patchTokenListing(tokId, {
        auctionID: listingId ? Number(listingId) : 0,
        price: sellTarget?.price ?? null,
      });
    }
  };

  // A listing ENDED (cancel / remove / early finalize) from the manage modal.
  // Clear the badge on that card straight away. The backend close is async and
  // may legitimately fail, so the UI reflects the confirmed on-chain fact
  // (the tx succeeded) and the 30s poller reconciles any indexing drift.
  const handleListingClosed = () => {
    const tokId = saleTarget?.tokenId ?? activeItem?.item?.tokId;
    if (tokId !== undefined && tokId !== null) {
      // 0, not null: the grid gates on `aid > 0`, and null would read as
      // "unknown" while 0 reads unambiguously as "no longer listed".
      patchTokenListing(tokId, { auctionID: 0 });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <Breadcrumb crumbs={[
        { name: 'Viri', url: '/' },
        { name: 'Collections', url: '/collections' },
        { name: resolvedCollectionName || 'Collection' },
      ]} logo={logo || stateImage} />

      <main className="main-content">
        <CollectionInfoHeader
          collectionId={cDat?.id}
          symbol={cDat?.symbol}
          name={cDat?.name}
          contractAddress={cDat?.contractAddress}
          network={cDat?.network}
          image={logo || stateImage || (cDat?.logo ? '/assets/img/' + cDat.logo : null)}
          totalSupply={cDat?.totalSupply ?? null}
          chainId={cDat?.network ?? cDat?.chainId ?? null}
          numHolders={cDat?.numHolders ?? null}
        />

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 40, height: 32, marginTop: 24, marginBottom: 10 }}>
          <button className={activeTab === 'items' ? 'active' : ''} style={{ padding: "0 17px 15px", background: 'transparent', border: 'none', color: activeTab === 'items' ? "#20ff43" : "#b6c1d8", textDecoration: "none", borderBottom: activeTab === 'items' ? "3px solid #20ff43" : "transparent", cursor: 'pointer', fontSize: 15 }} onClick={() => setActiveTab('items')}>Items</button>
          <button className={activeTab === 'activity' ? 'active' : ''} style={{ padding: "0 17px 15px", background: 'transparent', border: 'none', color: activeTab === 'activity' ? "#20ff43" : "#b6c1d8", textDecoration: "none", borderBottom: activeTab === 'activity' ? "3px solid #20ff43" : "transparent", cursor: 'pointer', fontSize: 15 }} onClick={() => setActiveTab('activity')}>Activity</button>
          <button className={activeTab === 'about' ? 'active' : ''} style={{ padding: "0 17px 15px", background: 'transparent', border: 'none', color: activeTab === 'about' ? "#20ff43" : "#b6c1d8", textDecoration: "none", borderBottom: activeTab === 'about' ? "3px solid #20ff43" : "transparent", cursor: 'pointer', fontSize: 15 }} onClick={() => setActiveTab('about')}>About</button>
        </div>

        <div style={{ maxWidth: 1470, margin: "auto", marginTop: 0, display: "flex", gap: 25 }}>
          {!filterCollapsed && (
            <aside className="collection-filter-panel pixel-card" style={{ width: 245, flexShrink: 0, background: '#0a1428', border: '1px solid #14548d', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }} onClick={() => setFilterCollapsed(true)}>
                <span style={{ fontSize: 17, fontWeight: 800, color: '#edf2ff' }}>Filters</span>
                <span style={{ fontSize: 12, color: '#a8b6cf' }}>▾</span>
              </div>
              <div style={{ padding: '0 18px 18px' }}>
                <VolumeStats volData={volData} volPer={volPer} onPeriodChange={handlePeriodChange} />
                <hr style={{ margin: '12px 0', borderColor: '#16325c' }} />
                <TraitRarityStats collId={cDat?.id} />
              </div>
            </aside>
          )}

          <div style={{ display: "flex", flexDirection: "column", minWidth: "0", flexGrow: 1 }}>
            {activeTab === 'items' && (<NFTPreviewGrid files={files} onItemSelect={handleItemSelect} failedImages={failedImages} onImageError={null} onPaginate={(dir) => alterPos(dir, currentPosition, setCurrentPosition)} currentPosition={currentPosition} searchValue={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Instant Search" pagination={pagination} />)}
            {activeTab === 'activity' && (<div className="pixel-card"><Comments itemHash={location.pathname} start={0} end={50} metaAction={"commented on the " + cDat?.symbol + " collection"} actionLocation={location.pathname} /></div>)}
            {activeTab === 'about' && (<div className="pixel-card"><VolumeStats volData={volData} volPer={volPer} onPeriodChange={handlePeriodChange} /><hr /><OwnedTokens cData={cDat} modalCallback={getItemAndShowModal} setMC={setMC} /><CollectionActions cData={cDat} mintItem={mintItem} item={[]} history={navigate} wallet={wallet} uploadCollImg={uploadCollImg} />{needsNetworkSwitch && (<div className="text-center mt-3"><button className="btn btn-warning" onClick={handleNetworkSwitch}>Switch to {chainData[cDat?.network].tokName} to see your items</button></div>)}</div>)}
          </div>
        </div>
      </main>

      {filterCollapsed && (
        <button aria-label="Show filters" onClick={() => setFilterCollapsed(false)} style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50, background: '#14548d', color: '#fff', border: 'none', borderRadius: 50, padding: '14px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.6)' }}>Filters ▸</button>
      )}

      <NftDetailModal
        show={show}
        onClose={() => setShow(false)}
        title={moTitle}
        item={activeItem?.item}
        imageUrl={activeItem?.imgUrl}
        chainIndex={cDat?.network}
        noMeta={activeItem?.noMeta}
        onTraitsSearch={doSearch}
        onNavigate={navigate}
        onBuy={handleBuy}
        onSell={handleSell}
        onManage={handleManage}
      />

      <SaleParticularsModal
        show={showSale}
        onClose={() => { setShowSale(false); setManageMode(false); }}
        title={
          manageMode
            ? `${moTitle ? `${moTitle} — ` : ''}Manage Listing`
            : `${moTitle ? `${moTitle} — ` : ''}Sale`
        }
        target={saleTarget}
        // Fired only after an on-chain close tx has MINED (cancel / remove /
        // early finalize), so the grid can clear this card's "For Sale" badge
        // without a page refetch.
        onListingChanged={handleListingClosed}
      />

      <SellItemModal
        show={showSell}
        onClose={() => { setShowSell(false); setSellTarget(null); }}
        title={`${moTitle ? `${moTitle} — ` : ''}List for sale`}
        target={sellTarget}
        onListed={handleListed}
      />
    </>
  );
};

export default CollectionContainer;
